// React Basic and Bootstrap
import React, { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Collapse,
  Container,
  Row,
} from 'reactstrap';

// v3 Screenshots
import ImageDiscover from '../../assets/images/v3/discover.png';
import ImageMyWords from '../../assets/images/v3/my_words.png';
import ImageWritingHelper from '../../assets/images/v3/writing_helper.png';
import ImageQuiz from '../../assets/images/v3/quiz.png';
import ImageWordOfDay from '../../assets/images/v3/word_of_day_notification.png';

import ImageAppStore from '../../assets/images/app_store_badge_564x168.png';
import ImageGooglePlay from '../../assets/images/google_play_badge_trimmed_564x168.png';
import ImageChrome from '../../assets/images/chrome.png';
import ImageFirefox from '../../assets/images/firefox.png';
import ImageEdge from '../../assets/images/edge.png';

import BackToTop from '../../components/Layout/backToTop';
import { FREE_VERSION, TUTORIAL_VIDEO_DURATION, TUTORIAL_VIDEO_ID } from '../../config';
import { getPlatform, STORE_LINKS } from '../../utils/platform';
import Pricing from './Pricing';
import Testi from './Testi';
import Section from './Section';
import ExtensionDemo from '../../components/Shared/ExtensionDemo';
import StoreBadges from '../../components/Shared/StoreBadges';

// The core screens exist in both the mobile app and the extension popup.
const CoreFeaturesIntro = () => (
  <Container className="feature-block core-features-intro px-4 px-md-3 px-lg-0">
    <Row className="justify-content-center">
      <Col lg={8} md={10} className="text-center">
        <h4 className="title mb-2" style={{ fontSize: '1.6rem' }}>
          The same words, on every device
        </h4>
        <p className="text-muted mb-0">
          Discover, your word list and quizzes are in the mobile app and in the browser extension&apos;s popup.
        </p>
      </Col>
    </Row>
  </Container>
);

const CORE_FEATURES = [
  {
    eyebrow: 'Discover',
    title: 'Find words you’ll actually use.',
    desc: 'Browse 50,000 words with clear definitions and examples. Filter by topic or level (A1–C2).',
    img: ImageDiscover,
    alt: 'Discover words filtered by topic and level',
  },
  {
    eyebrow: 'Saved words',
    title: 'Keep your vocabulary in one place.',
    desc: 'Save any word with one tap and come back to it whenever you need it.',
    img: ImageMyWords,
    alt: 'A saved word list',
    imageFirst: true,
  },
  {
    eyebrow: 'Practice',
    title: 'Practice the words you want to remember.',
    desc: 'Quick multiple-choice quizzes built from your own word list.',
    img: ImageQuiz,
    alt: 'A multiple-choice vocabulary quiz',
  },
];

const MORE_FEATURES = [
  {
    eyebrow: 'AI Writing Help',
    title: 'Write clearer, more natural English.',
    desc: 'Paste a sentence to get a more natural version, or describe what you want to say and HighVocab writes it.',
    img: ImageWritingHelper,
    alt: 'Writing helper fixing and composing sentences',
    imageFirst: true,
  },
  {
    eyebrow: 'Daily Reminder',
    title: 'A gentle nudge to practice every day.',
    desc: 'On your computer, a small reminder appears while you browse so you never skip a day.',
    img: ImageWordOfDay,
    alt: 'Word of the day notification popup',
  },
];

// One feature at a time with prev/next (and swipe on touch screens), instead of a long zig-zag.
const ALL_FEATURES = [...CORE_FEATURES, ...MORE_FEATURES];

const FeatureSlider = () => {
  const [index, setIndex] = useState(0);
  const count = ALL_FEATURES.length;
  const go = (step) => setIndex((i) => (i + step + count) % count);
  const f = ALL_FEATURES[index];
  const touchX = useRef(null);
  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
  };

  return (
    <Container className="feature-block feature-slider">
      <div className="feature-slider-tabs" role="tablist">
        {ALL_FEATURES.map((item, i) => (
          <button
            key={item.eyebrow}
            type="button"
            role="tab"
            aria-selected={i === index}
            className={i === index ? 'feature-slider-tab active' : 'feature-slider-tab'}
            onClick={() => setIndex(i)}
          >
            {item.eyebrow}
          </button>
        ))}
      </div>

      <div className="feature-slider-card" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <button type="button" className="feature-slider-btn prev" onClick={() => go(-1)} aria-label="Previous feature">
          <i className="mdi mdi-chevron-left" />
        </button>
        <Row className="align-items-center justify-content-center g-4">
          <Col md={5} key={f.eyebrow} className="feature-slider-text">
            <p className="feature-eyebrow">{f.eyebrow}</p>
            <h4 className="feature-title">{f.title}</h4>
            <p className="feature-desc">{f.desc}</p>
            <p className="feature-slider-count d-none d-md-block">
              {index + 1} / {count}
            </p>
          </Col>
          <Col md={5}>
            <div className="feature-slider-stage">
              <img key={f.eyebrow} src={f.img} className="feature-img" alt={f.alt} />
            </div>
          </Col>
        </Row>
        <button type="button" className="feature-slider-btn next" onClick={() => go(1)} aria-label="Next feature">
          <i className="mdi mdi-chevron-right" />
        </button>
        <div className="feature-slider-mobile-nav d-md-none">
          <button type="button" className="feature-slider-btn" onClick={() => go(-1)} aria-label="Previous feature">
            <i className="mdi mdi-chevron-left" />
          </button>
          <span className="feature-slider-count">
            {index + 1} / {count}
          </span>
          <button type="button" className="feature-slider-btn" onClick={() => go(1)} aria-label="Next feature">
            <i className="mdi mdi-chevron-right" />
          </button>
        </div>
      </div>
    </Container>
  );
};

const ExtensionSection = ({ isDesktop }) => (
  <div id="extension" className="extension-demo-section">
    <Container>
      <Row className="justify-content-center mb-4">
        <Col lg={8} md={10} className="text-center">
          <span className="extension-demo-label">For your computer</span>
          <h4 className="title mb-3" style={{ fontSize: '1.6rem' }}>
            Save a word. Recognize it next time.
          </h4>
          <p className="sec-title mb-0" style={{ maxWidth: 600, margin: '0 auto' }}>
            Use the HighVocab browser extension to save words while you read and highlight them when they appear again.
          </p>
        </Col>
      </Row>
      <ExtensionDemo />
      {!isDesktop && (
        <Row className="justify-content-center mt-4">
          <Col lg={8} md={10}>
            <p className="text-center small mb-2">
              <span className="fw-bold text-uppercase">How to use</span>
              <span className="text-muted"> · {TUTORIAL_VIDEO_DURATION}</span>
            </p>
            <div className="ratio ratio-16x9 shadow rounded overflow-hidden">
              <iframe
                src={`https://www.youtube.com/embed/${TUTORIAL_VIDEO_ID}?rel=0`}
                title="How to use HighVocab"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              ></iframe>
            </div>
          </Col>
        </Row>
      )}
      <div className="text-center mt-4">
        {isDesktop ? (
          <>
            <a
              href={STORE_LINKS.chrome}
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary d-inline-flex align-items-center"
              style={{ gap: 10 }}
            >
              <img src={ImageChrome} style={{ width: 20, height: 20 }} alt="" />
              Add to Chrome
            </a>
            <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
              <a href={STORE_LINKS.edge} target="_blank" rel="noreferrer" className="d-inline-flex align-items-center text-muted small" style={{ gap: 6 }}>
                <img src={ImageEdge} style={{ width: 16, height: 16 }} alt="" />
                Edge
              </a>
              <a href={STORE_LINKS.firefox} target="_blank" rel="noreferrer" className="d-inline-flex align-items-center text-muted small" style={{ gap: 6 }}>
                <img src={ImageFirefox} style={{ width: 16, height: 16 }} alt="" />
                Firefox
              </a>
            </div>
          </>
        ) : (
          <p className="text-muted small mb-0">
            Available for{' '}
            <a href={STORE_LINKS.chrome} target="_blank" rel="noreferrer">Chrome</a>,{' '}
            <a href={STORE_LINKS.edge} target="_blank" rel="noreferrer">Edge</a> and{' '}
            <a href={STORE_LINKS.firefox} target="_blank" rel="noreferrer">Firefox</a>{' '}
            on your computer.
          </p>
        )}
      </div>
    </Container>
  </div>
);

// Phones: a compact band with a direct store button — no QR codes.
const MobileAppCta = ({ platform }) => (
  <div className="mobile-app-cta text-center">
    <Container>
      <h4 className="feature-title mb-2">Start learning on your phone</h4>
      <p className="text-muted mb-3">Free to start · 50,000+ English words</p>
      <StoreBadges platform={platform} />
    </Container>
  </div>
);

const QrCard = ({ label, icon, url, badge, badgeAlt }) => (
  <Col xs={11} sm={8} md={5} lg={4}>
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(139,92,246,0.15)',
        borderRadius: 24,
        boxShadow: '0 8px 32px rgba(15,23,42,0.08)',
        padding: '32px 28px 24px',
        textAlign: 'center',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 20 }}>
        {icon}
        <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}>{label}</span>
      </div>
      <div style={{ display: 'inline-block', padding: 12, background: '#f8fafc', borderRadius: 16, border: '1px solid rgba(15,23,42,0.06)' }}>
        <QRCodeSVG value={url} size={160} bgColor="#f8fafc" fgColor="#0f172a" level="M" />
      </div>
      <div style={{ marginTop: 20 }}>
        <a href={url} target="_blank" rel="noreferrer" style={{ display: 'inline-block', lineHeight: 0 }}>
          <img src={badge} alt={badgeAlt} style={{ height: 44, width: 'auto' }} />
        </a>
      </div>
    </div>
  </Col>
);

// Desktop: scanning a QR code with your phone makes sense here.
const DesktopMobileAppSection = () => (
  <div
    id="mobile-app"
    className="feature-block"
    style={{
      background: 'linear-gradient(135deg, #f5f3ff 0%, #eef4ff 100%)',
      borderTop: '1px solid rgba(139,92,246,0.1)',
      borderBottom: '1px solid rgba(139,92,246,0.1)',
      padding: '64px 0',
    }}
  >
    <Container>
      <Row className="justify-content-center text-center mb-5">
        <Col lg={7} md={9}>
          <span
            style={{
              display: 'inline-block',
              background: 'rgba(139,92,246,0.1)',
              color: '#7c3aed',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.09em',
              borderRadius: 999,
              padding: '4px 14px',
              textTransform: 'uppercase',
              marginBottom: 16,
              border: '1px solid rgba(139,92,246,0.18)',
            }}
          >
            Now on Mobile
          </span>
          <h2 style={{ fontWeight: 700, color: '#0f172a', fontSize: 'clamp(1.6rem, 2.4vw, 2.2rem)', marginBottom: 12 }}>
            Take HighVocab Everywhere
          </h2>
          <p style={{ color: '#64748b', fontSize: '1.05rem', lineHeight: 1.7, marginBottom: 0 }}>
            Discover words, save your favorites, and take quizzes on your phone. Point your camera at the QR code to download.
          </p>
        </Col>
      </Row>

      <Row className="justify-content-center g-4">
        <QrCard
          label="iOS"
          url={STORE_LINKS.ios}
          badge={ImageAppStore}
          badgeAlt="Download on the App Store"
          icon={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#0f172a" xmlns="http://www.w3.org/2000/svg">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
          }
        />
        <QrCard
          label="Android"
          url={STORE_LINKS.android}
          badge={ImageGooglePlay}
          badgeAlt="Get it on Google Play"
          icon={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#3ddc84" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.523 15.341A5.98 5.98 0 0 0 18 13a5.98 5.98 0 0 0-.477-2.341l2.315-1.338a9 9 0 0 1 0 7.358l-2.315-1.338zM13 20.94V18a5.985 5.985 0 0 1-2-.341l-1.338 2.315A9.016 9.016 0 0 0 13 20.94zm-2.477-.966L9.185 17.66A5.98 5.98 0 0 1 7.523 15.341L5.208 16.68a9 9 0 0 0 5.315 3.294zM6 13a5.98 5.98 0 0 0 .477 2.341L4.162 16.68a9 9 0 0 1 0-7.358l2.315 1.338A5.98 5.98 0 0 0 6 13zm1.523-2.341L5.208 9.32a9.016 9.016 0 0 1 5.315-3.294L9.185 8.34A5.985 5.985 0 0 0 7.523 10.66zM11 5.06v2.94a5.985 5.985 0 0 1 2 .341l1.338-2.315A9.016 9.016 0 0 0 11 5.06zm2.477.966L14.815 8.34A5.98 5.98 0 0 1 16.477 10.66l2.315-1.338a9 9 0 0 0-5.315-3.296zM12 15a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/>
            </svg>
          }
        />
      </Row>
    </Container>
  </div>
);

const FaqItem = ({ question, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <Card className="border-0 rounded mb-2">
      <Link
        to="#"
        onClick={() => setOpen(!open)}
        className={open ? 'faq position-relative text-primary' : 'faq position-relative text-dark'}
      >
        <CardHeader className="border-0 shadow bg-light p-3">
          <h6 className="title mb-0">
            {question}
            <i className={open ? 'mdi mdi-chevron-up float-end' : 'mdi mdi-chevron-down float-end'} />
          </h6>
        </CardHeader>
      </Link>
      <Collapse isOpen={open}>
        <CardBody>
          <p className="text-muted mb-0 faq-ans">{children}</p>
        </CardBody>
      </Collapse>
    </Card>
  );
};

const Faq = () => (
  <section className="section pt-0" id="highvocab-faq">
    <Container className="mt-100 mt-60 md-padding-20">
      <Row className="justify-content-center">
        <Col xs={12} className="text-center">
          <div className="section-title">
            <h4 className="title mb-4">Common Questions</h4>
          </div>
        </Col>
      </Row>

      <Row className="justify-content-center">
        <Col lg={9} className="mt-4 pt-2">
          <div className="faq-content">
            <div className="accordion" id="accordionExample">
              <FaqItem question="Do I need a computer to use HighVocab?">
                No. The HighVocab app for iPhone and Android works on its own — discover words, save them, and practice with quizzes. On your computer, the browser extension for Chrome, Edge and Firefox has the same Discover, word list and quiz screens in its popup, and also lets you save and highlight words while you read websites.
              </FaqItem>
              <FaqItem question="How do I add a new word to my list?">
                In the app, tap the bookmark on any word in Discover. With the browser extension, click the word you want to learn on any website, tap the HighVocab icon that appears, then click &quot;Save word&quot;.
              </FaqItem>
              <FaqItem question="Can I see the full details of a word?">
                Yes. Tap any word in your list or in Discover to open the Word Detail page. You will see the full definition, example sentences, word forms, related words, and common phrases — all in one place.
              </FaqItem>
              <FaqItem question="Can I see definitions for highlighted words on a webpage?">
                Yes, with the browser extension. Hover over any highlighted word on a webpage and a pop-up will show the translation, pronunciation, definition, and an example sentence right away.
              </FaqItem>
              <FaqItem question="What languages can I translate into?">
                HighVocab is for English learners and supports translations into over 60 languages.
              </FaqItem>
              <FaqItem question="Can I export my word list?">
                Yes. You can export your vocabulary list as a CSV file, which you can open in Microsoft Excel or any spreadsheet program.
              </FaqItem>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  </section>
);

const Index = () => {
  const [platform] = useState(getPlatform);
  const isDesktop = platform === 'desktop';

  useEffect(() => {
    document.body.classList = '';
    const scrollNavigation = () => {
      var doc = document.documentElement;
      const navBar = document.getElementById('topnav');
      var top = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
      if (navBar != null) {
        if (top > 80) {
          navBar.classList.add('nav-sticky');
        } else {
          navBar.classList.remove('nav-sticky');
        }
      }
    };
    window.addEventListener('scroll', scrollNavigation, true);
    return () => window.removeEventListener('scroll', scrollNavigation, true);
  }, []);

  return (
    <React.Fragment>
      <Section platform={platform} />

      <div className="position-relative">
        <div className="shape overflow-hidden text-light">
          <svg viewBox="0 0 2880 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 48H1437.5H2880V0H2160C1442.5 52 720 0 720 0H0V48Z" fill="currentColor" />
          </svg>
        </div>
      </div>

      {isDesktop ? (
        <>
          <ExtensionSection isDesktop />
          <CoreFeaturesIntro />
          <FeatureSlider />
          <DesktopMobileAppSection />
        </>
      ) : (
        <>
          <CoreFeaturesIntro />
          <FeatureSlider />
          <MobileAppCta platform={platform} />
          <ExtensionSection isDesktop={false} />
        </>
      )}

      {!FREE_VERSION && <Pricing />}

      <Faq />

      <Testi platform={platform} />

      <div className="position-relative">
        <div className="shape overflow-hidden text-footer">
          <svg viewBox="0 0 2880 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 48H1437.5H2880V0H2160C1442.5 52 720 0 720 0H0V48Z" fill="currentColor" />
          </svg>
        </div>
      </div>

      <BackToTop />
    </React.Fragment>
  );
};

export default Index;
